import { type ValidationApiError } from "@anna-money/anna-web-lib/api";
import { FormValidationError } from "types/form";

// Backend gate message looks like "Address is not verified for director <name>".
const ADDRESS_NOT_VERIFIED_PATTERN = /address is not verified/i;

// `cause` is either a plain message or a field → message map (optionally one level nested), so match
// against every message rather than assuming the string shape.
export const isAddressNotVerifiedError = (error: FormValidationError): boolean => {
  const { cause } = error;
  if (typeof cause === "string") {
    return ADDRESS_NOT_VERIFIED_PATTERN.test(cause);
  }
  return Object.values(cause).some((value) =>
    typeof value === "string"
      ? ADDRESS_NOT_VERIFIED_PATTERN.test(value)
      : Object.values(value).some((nested) => ADDRESS_NOT_VERIFIED_PATTERN.test(nested)),
  );
};

interface ValidationError {
  title?: string;
  detail?: string;
}

interface ApiErrorResponse {
  message?: string;
  validation_errors?: ValidationError[];
}

interface ResponseData {
  error?: ApiErrorResponse;
}

interface HttpRequestDiagnosticsWithResponse {
  responseData?: ResponseData;
  [key: string]: unknown; // Allow other properties from HttpRequestDiagnostics
}

export const PRICING_PLAN_LOCKED_CODE = 1067;
export const PRICING_PLAN_LOCKED_MESSAGE = "Your plan can't be changed once payment has started.";

export const extractBackendValidationError = (error: ValidationApiError): FormValidationError => {
  if (error.code === PRICING_PLAN_LOCKED_CODE) {
    return new FormValidationError(PRICING_PLAN_LOCKED_MESSAGE);
  }

  // Extract validation errors directly from the API response structure
  const diagnostics = error.diagnostics as HttpRequestDiagnosticsWithResponse | undefined;

  if (diagnostics?.responseData?.error?.validation_errors) {
    const validationErrors = diagnostics.responseData.error.validation_errors;

    // Check if there are validation errors and get the first one
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      const firstError = validationErrors[0];

      // Use the title from the first validation error as the error message
      if (firstError?.title) {
        return new FormValidationError(firstError.title);
      }
    }
  }

  // Fallback to the general error message from the API
  const generalErrorMessage = diagnostics?.responseData?.error?.message || error.message;

  return new FormValidationError(generalErrorMessage);
};
